import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { Link } from "react-router-dom";
import api from "@/lib/api";

const GEO_URL = "https://cdn.jsdelivr.net/npm/us-atlas@3/states-10m.json";

export default function Roadmap() {
    const [roadmap, setRoadmap] = useState([]);
    const [hovered, setHovered] = useState(null);

    useEffect(() => { api.get("/roadmap").then(r => setRoadmap(r.data)).catch(() => {}); }, []);

    const byName = useMemo(() => {
        const map = {};
        roadmap.forEach(r => { map[r.name] = r; });
        return map;
    }, [roadmap]);

    const hoverInfo = hovered ? byName[hovered] : null;

    return (
        <div className="pt-[68px]" data-testid="roadmap-page">
            <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-24 pb-12">
                <div className="overline mb-6">[ Roadmap ] The national circuit</div>
                <h1 className="font-display font-black tracking-tighter leading-[0.88] text-5xl md:text-[9vw]">
                    Fifty <br />
                    <span className="text-outline-strong">states.</span>
                </h1>
                <p className="mt-8 text-secondary max-w-xl text-base md:text-lg leading-relaxed">
                    We're crowning a champion in every U.S. state — and every state is live right now. Pick your home state, run the qualifier, take the crown. Every champion advances to the national final.
                </p>
            </section>

            <section className="max-w-[1600px] mx-auto px-6 md:px-10 grid md:grid-cols-12 gap-8 pb-24">
                {/* Map */}
                <motion.div
                    initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                    className="md:col-span-8 border border-white/10 clip-frame bg-[#0A0A0A]/70 p-4 md:p-6 relative"
                    data-testid="roadmap-map"
                >
                    <ComposableMap projection="geoAlbersUsa" width={980} height={560} style={{ width: "100%", height: "auto" }}>
                        <Geographies geography={GEO_URL}>
                            {({ geographies }) =>
                                geographies.map(geo => {
                                    const name = geo.properties.name;
                                    const isHover = hovered === name;
                                    return (
                                        <Geography
                                            key={geo.rsmKey}
                                            geography={geo}
                                            onMouseEnter={() => setHovered(name)}
                                            onMouseLeave={() => setHovered(null)}
                                            style={{
                                                default: { fill: "#2A1547", stroke: "#A855F7", strokeWidth: 0.7, outline: "none", transition: "fill 240ms ease" },
                                                hover:   { fill: "#A855F7", stroke: "#FFFFFF", strokeWidth: 1.2, outline: "none", cursor: "pointer" },
                                                pressed: { fill: "#A855F7", stroke: "#FFFFFF", strokeWidth: 1.2, outline: "none" },
                                            }}
                                        />
                                    );
                                })
                            }
                        </Geographies>
                    </ComposableMap>

                    {/* Live indicator overlay */}
                    <div className="absolute top-6 right-6 flex items-center gap-2 font-mono text-[10px] tracking-[0.24em] uppercase text-signal" data-testid="roadmap-live-indicator">
                        <span className="w-1.5 h-1.5 rounded-full bg-signal live-dot" /> 50 states · Live
                    </div>

                    {/* Legend */}
                    <div className="mt-4 flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 border-t border-white/5">
                        <LegendDot color="#2A1547" outline="#A855F7" label="Live · Open qualifiers" />
                        <LegendDot color="#A855F7" label="Your pick" />
                        <div className="ml-auto font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">Hover a state</div>
                    </div>
                </motion.div>

                {/* Side panel */}
                <div className="md:col-span-4 flex flex-col gap-4">
                    <motion.div
                        key={hoverInfo?.code || "default"}
                        initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
                        className="border border-white/10 clip-frame p-6 bg-[#0A0A0A] min-h-[220px]"
                        data-testid="roadmap-detail-panel"
                    >
                        {hoverInfo ? (
                            <>
                                <div className="overline mb-3 text-signal">LIVE · {hoverInfo.date}</div>
                                <h3 className="font-display font-black tracking-tighter text-4xl leading-[0.9]">{hoverInfo.name}</h3>
                                <div className="mt-2 font-mono text-xs text-white/40 tracking-[0.2em]">/ {hoverInfo.code}</div>
                                <p className="mt-6 text-secondary text-sm leading-relaxed">{hoverInfo.note}</p>
                                <Link to="/tournaments" className="mt-6 inline-block font-mono text-[11px] tracking-[0.24em] uppercase text-volt hover:text-white transition-colors" data-testid="roadmap-view-brackets">
                                    View {hoverInfo.name} brackets →
                                </Link>
                            </>
                        ) : (
                            <>
                                <div className="overline mb-3 text-signal">Season 1 · All live</div>
                                <h3 className="font-display font-black tracking-tighter text-4xl leading-[0.9]">Your state is live.</h3>
                                <div className="mt-2 font-mono text-xs text-white/40 tracking-[0.2em]">/ 50 CIRCUITS</div>
                                <p className="mt-6 text-secondary text-sm leading-relaxed">
                                    Open Modern Warfare 4 qualifiers — Warzone squads and 4v4 Multiplayer — running in all 50 states right now. Top 30 in each state advance to the semifinals. Champions take the national stage.
                                </p>
                                <Link to="/tournaments" className="mt-6 inline-block font-mono text-[11px] tracking-[0.24em] uppercase text-volt hover:text-white transition-colors" data-testid="roadmap-view-brackets-default">
                                    View all brackets →
                                </Link>
                            </>
                        )}
                    </motion.div>

                    {/* What comes after */}
                    <div className="border border-white/10 clip-frame p-6 bg-[#050505]" data-testid="roadmap-schedule">
                        <div className="overline mb-4">/ The road ahead</div>
                        <ul className="space-y-3">
                            {[
                                ["State Qualifiers", "Live now", true],
                                ["State Semifinals", "After qualifiers close", false],
                                ["State Championships", "Top squads per state", false],
                                ["The National Final", "100 state champions", false],
                            ].map(([label, note, live]) => (
                                <li key={label} className="flex items-center justify-between text-sm">
                                    <div className="flex items-center gap-3">
                                        <span className={`w-1.5 h-1.5 rounded-full ${live ? "bg-signal live-dot" : "bg-white/25"}`} />
                                        <span className="text-white/85">{label}</span>
                                    </div>
                                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/40">{note}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>
        </div>
    );
}

const LegendDot = ({ color, outline, label }) => (
    <div className="flex items-center gap-2">
        <span className="w-3 h-3 border" style={{ backgroundColor: color, borderColor: outline || color }} />
        <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-white/50">{label}</span>
    </div>
);
